import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, ChevronRight, Search, ShieldCheck, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api, inr } from "@/lib/mro";

const statusTone = {
  approved: "bg-emerald-500/15 text-emerald-500 border-emerald-500/30",
  rejected: "bg-rose-500/15 text-rose-500 border-rose-500/30",
  pending: "bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30",
};

export default function Standardization({ plant }) {
  const [clusters, setClusters] = useState(null);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [canonical, setCanonical] = useState("");
  const [note, setNote] = useState("");
  const navigate = useNavigate();

  const load = async () => {
    const r = await api.get("/clusters", { params: { plant, status, search: search || undefined } });
    setClusters(r.data.clusters);
    setSelected((prev) => r.data.clusters.find((c) => c.cluster_id === prev?.cluster_id) || r.data.clusters[0] || null);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plant, status]);

  useEffect(() => {
    if (selected) setCanonical(selected.approved_canonical_name || selected.canonical_name);
  }, [selected]);

  const decide = async (decision) => {
    await api.post(`/clusters/${selected.cluster_id}/decision`, { decision, canonical_name: canonical, note });
    toast.success(decision === "approved" ? "Golden master approved" : "Cluster rejected — items stay separate");
    setNote("");
    load();
  };

  if (!clusters) return <div className="grid gap-4 lg:grid-cols-12"><Skeleton className="h-[520px] rounded-xl lg:col-span-5" /><Skeleton className="h-[520px] rounded-xl lg:col-span-7" /></div>;

  return (
    <div className="space-y-5" data-testid="standardization-page">
      <div>
        <h1 className="font-display text-2xl font-extrabold tracking-tight">Material standardization workbench</h1>
        <p className="text-sm text-muted-foreground">Probable-equivalent descriptions are clustered with a confidence score. Nothing merges until an SME approves — this builds a governed golden material master.</p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="cluster-search" className="pl-9" placeholder="Search description, part number, canonical name…" value={search}
            onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="cluster-status-filter" className="h-10 w-[170px] text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="pending">Pending review</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" data-testid="cluster-search-btn" onClick={load}>Apply</Button>
      </div>

      <div className="grid gap-5 lg:grid-cols-12">
        <div className="panel overflow-hidden lg:col-span-5" data-testid="cluster-list">
          <div className="border-b border-border px-4 py-3 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
            {clusters.length} duplicate clusters
          </div>
          <div className="max-h-[620px] divide-y divide-border overflow-y-auto">
            {clusters.map((c) => (
              <button key={c.cluster_id} data-testid={`cluster-item-${c.cluster_id}`} onClick={() => setSelected(c)}
                className={`w-full px-4 py-3 text-left transition-colors hover:bg-accent/40 ${selected?.cluster_id === c.cluster_id ? "bg-accent/60" : ""}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold">{c.approved_canonical_name || c.canonical_name}</div>
                    <div className="mt-0.5 truncate font-mono text-[11px] text-muted-foreground">
                      {c.member_count} variants · {c.plants.join(", ")}
                    </div>
                  </div>
                  <div className="shrink-0 text-right">
                    <div className="font-mono text-xs font-bold">{c.confidence}%</div>
                    <div className="font-mono text-[10px] text-muted-foreground">{inr(c.total_inventory_value)}</div>
                  </div>
                </div>
                <div className="mt-2 flex items-center gap-1.5">
                  <Badge variant="outline" className={`${statusTone[c.status]} text-[10px]`}>{c.status}</Badge>
                  {c.needs_sme_review && c.status === "pending" && (
                    <Badge variant="outline" className="border-violet-500/30 bg-violet-500/15 text-[10px] text-violet-500">SME review</Badge>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>

        {selected && (
          <div className="space-y-5 lg:col-span-7" data-testid="cluster-detail">
            <div className="panel p-5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Suggested canonical material</div>
                  <h3 className="mt-1 font-display text-lg font-bold" data-testid="canonical-name">{selected.approved_canonical_name || selected.canonical_name}</h3>
                </div>
                <Badge variant="outline" className={statusTone[selected.status]} data-testid="cluster-status-badge">{selected.status}</Badge>
              </div>
              <div className="mt-4 grid gap-3 sm:grid-cols-4">
                {[["Manufacturer", selected.manufacturer], ["Part number", selected.part_number], ["Category", selected.category], ["UOM", selected.uom]].map(([l, v]) => (
                  <div key={l}>
                    <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{l}</div>
                    <div className="font-mono text-sm font-semibold">{v}</div>
                  </div>
                ))}
              </div>
              <div className="mt-3">
                <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Specification extracted from descriptions</div>
                <div className="text-sm">{selected.specification}</div>
              </div>
              <div className="mt-4">
                <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                  <span>Match confidence</span><span data-testid="confidence-value">{selected.confidence}%</span>
                </div>
                <Progress value={selected.confidence} className="mt-1.5 h-2" />
              </div>
            </div>

            <div className="panel overflow-hidden" data-testid="cluster-members-table">
              <div className="border-b border-border px-4 py-3">
                <h4 className="font-display text-sm font-semibold">Plant-specific records identified as probable equivalents</h4>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/60">
                    <tr className="text-left font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                      <th className="px-4 py-2.5">Original description</th>
                      <th className="px-4 py-2.5">Material code</th>
                      <th className="px-4 py-2.5">Plant</th>
                      <th className="px-4 py-2.5 text-right">Qty</th>
                      <th className="px-4 py-2.5 text-right">Value</th>
                      <th className="px-4 py-2.5 text-right">Match</th>
                      <th className="px-4 py-2.5" />
                    </tr>
                  </thead>
                  <tbody>
                    {selected.members.map((m) => (
                      <tr key={m.id} className="border-t border-border hover:bg-accent/30" data-testid={`cluster-member-${m.id}`}>
                        <td className="px-4 py-3">
                          <div className="font-mono">{m.description}</div>
                          <div className="text-[11px] text-muted-foreground">
                            extracted: {m.extracted.manufacturer || "—"} / {m.extracted.part_number || "—"} / {m.extracted.category || "—"}
                          </div>
                        </td>
                        <td className="px-4 py-3 font-mono text-xs">{m.material_code}<div className="text-[10px] text-muted-foreground">{m.legacy_code}</div></td>
                        <td className="px-4 py-3">{m.plant}</td>
                        <td className="px-4 py-3 text-right font-mono tabular">{m.qty}</td>
                        <td className="px-4 py-3 text-right font-mono tabular">{inr(m.value)}</td>
                        <td className="px-4 py-3 text-right font-mono tabular">{m.match_score}%</td>
                        <td className="px-4 py-3 text-right">
                          <Button size="sm" variant="ghost" data-testid={`open-part-${m.id}`} onClick={() => navigate(`/part/${m.id}`)}>
                            360 <ChevronRight className="ml-1 h-3.5 w-3.5" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="panel p-5" data-testid="sme-workflow">
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-primary" />
                <h4 className="font-display text-sm font-semibold">SME approval workflow</h4>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">AI never silently merges items. Edit the canonical name, record your rationale, then approve or reject.</p>
              <Input className="mt-3 font-mono text-sm" data-testid="canonical-input" value={canonical} onChange={(e) => setCanonical(e.target.value)} />
              <Textarea className="mt-2 text-sm" rows={2} data-testid="sme-note" placeholder="Reviewer rationale (stored in the audit trail)…" value={note} onChange={(e) => setNote(e.target.value)} />
              <div className="mt-3 flex flex-wrap gap-2">
                <Button size="sm" data-testid="approve-cluster" onClick={() => decide("approved")}><Check className="mr-1.5 h-3.5 w-3.5" />Approve golden master</Button>
                <Button size="sm" variant="outline" data-testid="reject-cluster" onClick={() => decide("rejected")}><X className="mr-1.5 h-3.5 w-3.5" />Reject / keep separate</Button>
              </div>
              {selected.governance?.decided_at && (
                <div className="mt-3 rounded-lg border border-border bg-muted/40 p-3 font-mono text-[11px] text-muted-foreground" data-testid="governance-audit">
                  v{selected.governance.version} · {selected.governance.decision} by {selected.governance.reviewer} · {new Date(selected.governance.decided_at).toLocaleString()}
                  {selected.governance.note ? ` · "${selected.governance.note}"` : ""}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
